import React, { useEffect, useState } from "react";

export default function AdminDashboard() {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = React.useState(1);

  const BASE_URL =
    process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        const res = await fetch(`${BASE_URL}/api/admin/dashboard`, {
          method: "GET",
          credentials: "include", // Send HttpOnly cookie
        });

        if (!res.ok) throw new Error("Failed to fetch dashboard data");

        const data = await res.json();
        setDashboardData(data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboard();
  }, [BASE_URL]);

  if (loading) return <div className="p-6">Loading dashboard...</div>;
  if (!dashboardData)
    return (
      <div className="text-center p-6 text-red-500">
        Error loading data.
      </div>
    );

  // Safe fallbacks
  const totalOrders = dashboardData.totalOrders ?? 0;
  const activeDrivers = dashboardData.activeDrivers ?? 0;

  const lowStockItems = dashboardData.lowStockItems ?? [];

  return (
    <div className="max-w-6xl mx-auto bg-white p-6 rounded shadow">
      <h1 className="text-2xl font-bold mb-6">Admin Dashboard</h1>

      {/* KPI Cards */}
      <div className="grid md:grid-cols-2 gap-6 mb-10">
        <div className="bg-blue-50 p-4 rounded shadow text-center">
          <h2 className="text-lg font-semibold">Total Orders</h2>
          <p className="text-3xl font-bold text-blue-600 mt-2">
            {totalOrders.toLocaleString()}
          </p>
        </div>
        <div className="bg-purple-50 p-4 rounded shadow text-center">
          <h2 className="text-lg font-semibold">Active Drivers</h2>
          <p className="text-3xl font-bold text-purple-600 mt-2">
            {activeDrivers.toLocaleString()}
          </p>
        </div>
      </div>

      {/* Orders Overview */}
      <div className="mb-10">
        <h2 className="text-xl font-semibold mb-4">Orders Overview</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {Object.entries(dashboardData.orderStatus).map(([status, count], idx) => {
            // Define different color schemes
            const colors = [
              { bg: "bg-pink-100", text: "text-pink-800", label: "text-pink-700" },
              { bg: "bg-yellow-100", text: "text-yellow-800", label: "text-yellow-700" },
              { bg: "bg-red-100", text: "text-red-800", label: "text-red-700" },
              { bg: "bg-blue-100", text: "text-blue-800", label: "text-blue-700" },
              { bg: "bg-purple-100", text: "text-purple-800", label: "text-purple-700" },
              { bg: "bg-green-100", text: "text-green-800", label: "text-green-700" },
            ];
            const color = colors[idx % colors.length];

            return (
              <div key={idx} className={`${color.bg} p-4 rounded shadow text-center`}>
                <p className={`text-sm font-medium ${color.label}`}>
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </p>
                <p className={`text-xl font-bold ${color.text}`}>
                  {count.toLocaleString()}
                </p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Low Stock Alert with Pagination */}
      <div className="mb-10">
        <h2 className="text-xl font-semibold mb-4">Low Stock Alert</h2>

        {lowStockItems.length > 0 ? (
          (() => {
            const sortedItems = [...lowStockItems].sort((a, b) => {
              const aQty = a.stockQty ?? 0;
              const bQty = b.stockQty ?? 0;
              if (aQty === 0 && bQty !== 0) return -1;
              if (aQty !== 0 && bQty === 0) return 1;
              return aQty - bQty;
            });

            const itemsPerPage = 10;
            const totalPages = Math.ceil(sortedItems.length / itemsPerPage);

            const startIdx = (currentPage - 1) * itemsPerPage;
            const currentItems = sortedItems.slice(startIdx, startIdx + itemsPerPage);

            const handlePrev = () => {
              if (currentPage > 1) setCurrentPage((prev) => prev - 1);
            };

            const handleNext = () => {
              if (currentPage < totalPages) setCurrentPage((prev) => prev + 1);
            };

            return (
              <div>
                <ul className="space-y-2">
                  {currentItems.map((item, idx) => {
                    const qty = item.stockQty ?? 0;
                    const isOutOfStock = qty === 0;
                    return (
                      <li
                        key={idx}
                        className={`px-4 py-2 rounded flex items-start gap-4 ${
                          isOutOfStock
                            ? "bg-red-100 text-red-700"
                            : "bg-yellow-100 text-yellow-800"
                        }`}
                      >
                        {/* Item Name */}
                        <span
                          className="
                            flex-1 font-medium leading-snug
                            line-clamp-2 min-h-[2.6em]
                            md:line-clamp-none md:min-h-0
                          "
                        >
                          {item.name}
                        </span>

                        {/* Quantity */}
                        <span className="flex-shrink-0 font-bold whitespace-nowrap">
                          {isOutOfStock ? "Out of Stock" : `${qty.toLocaleString()} left`}
                        </span>
                      </li>
                    );
                  })}
                </ul>

                {/* Pagination Controls */}
                <div className="flex justify-center items-center gap-4 mt-4">
                  <button
                    onClick={handlePrev}
                    disabled={currentPage === 1}
                    className={`px-3 py-1 rounded ${
                      currentPage === 1
                        ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                        : "bg-blue-500 text-white hover:bg-blue-600"
                    }`}
                  >
                    Prev
                  </button>

                  <span className="text-sm text-gray-600">
                    Page {currentPage} of {totalPages}
                  </span>

                  <button
                    onClick={handleNext}
                    disabled={currentPage === totalPages}
                    className={`px-3 py-1 rounded ${
                      currentPage === totalPages
                        ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                        : "bg-blue-500 text-white hover:bg-blue-600"
                    }`}
                  >
                    Next
                  </button>
                </div>
              </div>
            );
          })()
        ) : (
          <p className="text-gray-500">No low stock items.</p>
        )}
      </div>
    </div>
  );
}
